import React, {createContext, useContext, useEffect, useReducer} from 'react';
import {AuthContext} from './AuthContext';
import {useGetAxiosClient} from 'hooks/useGetEndpoint';
import reducer from './UserReducer';
import axios from 'axios';
import validateAddress from 'utils/validateAddress';

const initialState = {
    userState: 'loggedOut',
    user: null,
    isUserLoading: false,
    mmlds: {
        mmld_docs: {},
        recent_mmld_meta: [],
        user_endorsements: [],
    },
};

// Create Context
const UserContext = createContext(initialState);

// Provider component
const UserProvider = ({children}) => {
    const {firebaseAuth, authToken, isLoading, logOut, sendVerificationEmail} =
        useContext(AuthContext);
    const {ready} = useGetAxiosClient();

    const [state, dispatch] = useReducer(reducer, initialState);

    const actions = {
        userIsLoading: () => {
            dispatch({type: 'USER_IS_LOADING'});
        },
        userNotLoading: () => {
            dispatch({type: 'USER_NOT_LOADING'});
        },
        fetchUserFail: payload => {
            dispatch({type: 'FETCH_USER_FAIL', payload});
        },
        userFetched: user => {
            dispatch({type: 'USER_FETCHED', payload: user});
            window.dataLayer.push({
                userId: user.id,
                data: null,
            });
        },
        userEmailNotVerified: () => {
            dispatch({type: 'USER_EMAIL_NOT_VERIFIED'});
        },
        userNotOnTestList: () => {
            dispatch({type: 'USER_NOT_ON_TEST_LIST'});
        },
        updateSubscription: payload => {
            dispatch({type: 'UPDATE_SUBSCRIPTION', payload: payload});
        },
        updateUser: user => {
            dispatch({type: 'UPDATE_USER', user: user});
        },
        updateProfile: profile => {
            dispatch({type: 'UPDATE_PROFILE', profile: profile});
        },
        updatePayment: payment => {
            dispatch({type: 'UPDATE_PAYMENT', payment});
        },
        updateBillingInfo: billingInfo => {
            dispatch({type: 'UPDATE_BILLING_INFO', billingInfo});
        },
        logUserOut: () => {
            logOut();
            dispatch({type: 'LOGOUT'});
            window.dataLayer.push({
                userId: null,
                data: null,
            });
        },
        register: () => {
            dispatch({type: 'REGISTER'});
            window.dataLayer.push({
                event: 'user-register',
                data: null,
            });
        },
        togglePro: () => {
            dispatch({type: 'TOGGLE_PRO'});
        },
    };

    const getUser = async authToken => {
        actions.userIsLoading();
        try {
            const headers = {
                Authorization: authToken,
            };
            const masquerade = sessionStorage.getItem('admin.masquerade');
            if (masquerade) {
                headers['X-EMAIL'] = masquerade;
            }
            const result = await axios.get('/api/1/user', {
                headers,
            });

            if (result?.data?.status === 'error') {
                actions.fetchUserFail();
            }
            // User authenticated properly and is on test user list but has not
            // verified their email
            // Set EmailNotVerified and send verification email.
            else if (result?.data?.emailNotVerified) {
                actions.userEmailNotVerified();
                sendVerificationEmail();

                // User is on test user list and has verified email but has not created profile
                // or
                // User has an account and a profile
                // or
                // User has an account but the account is in some deactivated state
            } else if (result?.data?.user) {
                actions.userFetched(result?.data);
            }

            // setIsOnboarded(result?.data?.isOnboarded);
        } catch (error) {
            if (error?.response?.status === 412) {
                // User is not on test user list
                actions.userNotOnTestList();
            }
        } finally {
            actions.userNotLoading();
        }
    };

    const refetchUser = async () => {
        try {
            const headers = {
                Authorization: authToken,
            };
            const masquerade = sessionStorage.getItem('admin.masquerade');
            if (masquerade) {
                headers['X-EMAIL'] = masquerade;
            }
            const result = await axios.get('/api/1/user', {
                headers,
            });

            if (result?.data?.user) {
                actions.userFetched(result?.data);
            }

            // setIsOnboarded(result?.data?.isOnboarded);
        } catch (error) {
            if (error) {
                // User is not on test user list
                console.debug('user refetch error', error);
            }
        } finally {
            actions.userNotLoading();
        }
    };

    useEffect(() => {
        if (firebaseAuth && authToken) {
            getUser(authToken);
        } else if (!firebaseAuth && !authToken && !isLoading) {
            actions.logUserOut();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebaseAuth, authToken]);

    const hasPaymentInfo = !!(state.payment && state.payment?.maskedAccount);
    const hasBillingInfo = validateAddress(state?.billingInfo);
    const deliveryInfo =
        Object.fromEntries(
            Object.entries(state?.profile ?? {})?.filter(([key, val]) =>
                key.includes('delivery_address')
            ) ?? []
        ) ?? {};
    const hasDeliveryInfo = validateAddress(deliveryInfo);
    const isVIP = state.user?.permissions?.roles.includes('vip');
    const canAccessWithoutPaymentInfo =
        state.user?.permissions?.canAccessWithoutPaymentInfo;
    const paymentRequired = !(isVIP || canAccessWithoutPaymentInfo);
    const hasActiveApplication = !!state?.applications?.application_active;
    const hasMmlds = Boolean(Object.keys(state?.mmlds?.mmld_docs)?.length);

    return (
        <UserContext.Provider
            displayName='User'
            value={{
                ...state,
                axiosReady: ready,
                subscription: state.subscription_info,
                updateSubscription: actions.updateSubscription,
                updateUser: actions.updateUser,
                updateProfile: actions.updateProfile,
                refetchUser,
                hasMmlds,
                hasPaymentInfo,
                hasBillingInfo,
                deliveryInfo,
                hasDeliveryInfo,
                isVIP,
                canAccessWithoutPaymentInfo,
                paymentRequired,
                hasActiveApplication,
                togglePro: actions.togglePro,
                actions,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
export {UserContext};
